/**
 * RiskMatrix — Risk × Complexity quadrant scatter chart.
 *
 * X axis: Complexity Score (100-500)
 * Y axis: Risk Score (100-500)
 * Quadrant backgrounds via four SVG rect overlays.
 * Dots colored by RCACategory. Size optionally scaled by budget.
 */

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import type { RCACategory, RiskLevel } from '@/types/rca';

// ============================================================
// Constants
// ============================================================

const THRESHOLD = 300; // midpoint for quadrant split (approx medium/high boundary)

const CATEGORY_COLORS: Record<RCACategory, string> = {
  1: '#00B37E',
  2: '#60B0E0',
  3: '#E5A000',
  4: '#E5304A',
};

const QUADRANT_FILLS = {
  managedFill: 'rgba(0, 179, 126, 0.06)',   // low risk / low complexity — green
  watchFill: 'rgba(229, 160, 0, 0.08)',      // high risk / low complexity — amber
  complexFill: 'rgba(96, 176, 224, 0.08)',   // low risk / high complexity — blue
  criticalFill: 'rgba(229, 48, 74, 0.08)',   // high risk / high complexity — red
};

// ============================================================
// Types
// ============================================================

interface ProjectPoint {
  id: string;
  name: string;
  avgRiskScore: number;
  avgComplexityScore: number;
  category: RCACategory;
  budget?: number;
}

interface RiskMatrixProps {
  projects: ProjectPoint[];
  onProjectClick?: (projectId: string) => void;
}

// ============================================================
// Custom Tooltip
// ============================================================

interface TooltipPayloadItem {
  payload?: ProjectPoint;
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayloadItem[] }) {
  if (!active || !payload || payload.length === 0) return null;
  const pt = payload[0]?.payload;
  if (!pt) return null;

  const riskLabel: RiskLevel =
    pt.avgRiskScore >= 368 ? 'High' : pt.avgRiskScore >= 234 ? 'Medium' : 'Low';
  const complexityLabel: RiskLevel =
    pt.avgComplexityScore >= 368 ? 'High' : pt.avgComplexityScore >= 234 ? 'Medium' : 'Low';

  return (
    <div className="rounded-lg border border-border bg-card shadow-medium px-3 py-2 text-sm max-w-[220px]">
      <p className="font-semibold text-foreground mb-1 truncate">{pt.name}</p>
      <p className="text-muted-foreground">
        Risk: <span className="text-foreground font-medium">{riskLabel} ({Math.round(pt.avgRiskScore)})</span>
      </p>
      <p className="text-muted-foreground">
        Complexity: <span className="text-foreground font-medium">{complexityLabel} ({Math.round(pt.avgComplexityScore)})</span>
      </p>
      <p className="text-muted-foreground">
        Category:{' '}
        <span
          className="font-bold"
          style={{ color: CATEGORY_COLORS[pt.category] }}
        >
          Cat {pt.category}
        </span>
      </p>
    </div>
  );
}

// ============================================================
// Quadrant background overlay (SVG foreignObject equivalent via recharts customized dot)
// We use four reference areas rendered as custom SVG shapes injected via
// a custom background layer in the scatter chart.
// ============================================================

interface QuadrantBackgroundProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  xAxisMin: number;
  xAxisMax: number;
  yAxisMin: number;
  yAxisMax: number;
  threshold: number;
}

function QuadrantBackground({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  threshold,
  xAxisMin,
  xAxisMax,
  yAxisMin,
  yAxisMax,
}: QuadrantBackgroundProps) {
  // Map domain coords → pixel coords
  const xScale = (v: number) => x + ((v - xAxisMin) / (xAxisMax - xAxisMin)) * width;
  const yScale = (v: number) => y + ((yAxisMax - v) / (yAxisMax - yAxisMin)) * height;

  const midX = xScale(threshold);
  const midY = yScale(threshold);

  const leftW = midX - x;
  const rightW = x + width - midX;
  const topH = midY - y;
  const bottomH = y + height - midY;

  const quadrants = [
    // Bottom-left: low complexity, low risk — Managed (green)
    { rx: x, ry: midY, rw: leftW, rh: bottomH, fill: QUADRANT_FILLS.managedFill, label: 'Managed', lx: x + leftW / 2, ly: y + height - 12 },
    // Top-left: low complexity, high risk — Watch (amber)
    { rx: x, ry: y, rw: leftW, rh: topH, fill: QUADRANT_FILLS.watchFill, label: 'Watch', lx: x + leftW / 2, ly: y + 16 },
    // Bottom-right: high complexity, low risk — Complex (blue)
    { rx: midX, ry: midY, rw: rightW, rh: bottomH, fill: QUADRANT_FILLS.complexFill, label: 'Complex', lx: midX + rightW / 2, ly: y + height - 12 },
    // Top-right: high complexity, high risk — Critical (red)
    { rx: midX, ry: y, rw: rightW, rh: topH, fill: QUADRANT_FILLS.criticalFill, label: 'Critical', lx: midX + rightW / 2, ly: y + 16 },
  ];

  return (
    <g>
      {quadrants.map((q) => (
        <g key={q.label}>
          <rect x={q.rx} y={q.ry} width={q.rw} height={q.rh} fill={q.fill} />
          <text
            x={q.lx}
            y={q.ly}
            textAnchor="middle"
            fontSize={10}
            fontWeight={600}
            fill="currentColor"
            opacity={0.35}
          >
            {q.label}
          </text>
        </g>
      ))}
    </g>
  );
}

// ============================================================
// Main Component
// ============================================================

export function RiskMatrix({ projects, onProjectClick }: RiskMatrixProps) {
  // Budget range for dot sizing
  const budgets = projects.map((p) => p.budget ?? 0).filter((b) => b > 0);
  const maxBudget = budgets.length > 0 ? Math.max(...budgets) : 0;

  function dotSize(budget?: number): number {
    if (!maxBudget || !budget) return 8;
    return 6 + (budget / maxBudget) * 14;
  }

  // Scatter data: recharts uses x/y field names by default but we override via dataKey
  const data = projects.map((p) => ({
    ...p,
    x: p.avgComplexityScore,
    y: p.avgRiskScore,
  }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <ScatterChart margin={{ top: 20, right: 30, bottom: 30, left: 30 }}>
        {/* Quadrant backgrounds */}
        <defs>
          <QuadrantBackground
            xAxisMin={100}
            xAxisMax={500}
            yAxisMin={100}
            yAxisMax={500}
            threshold={THRESHOLD}
          />
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />

        <XAxis
          type="number"
          dataKey="x"
          name="Complexity"
          domain={[100, 500]}
          label={{
            value: 'Complexity Score',
            position: 'insideBottom',
            offset: -15,
            style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 },
          }}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
        />

        <YAxis
          type="number"
          dataKey="y"
          name="Risk"
          domain={[100, 500]}
          label={{
            value: 'Risk Score',
            angle: -90,
            position: 'insideLeft',
            offset: 15,
            style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 },
          }}
          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
        />

        {/* Threshold lines */}
        <ReferenceLine
          x={THRESHOLD}
          stroke="hsl(var(--muted-foreground))"
          strokeDasharray="6 3"
          opacity={0.5}
        />
        <ReferenceLine
          y={THRESHOLD}
          stroke="hsl(var(--muted-foreground))"
          strokeDasharray="6 3"
          opacity={0.5}
        />

        <Tooltip content={<CustomTooltip />} cursor={false} />

        <Scatter
          data={data}
          onClick={(point) => {
            const proj = point as unknown as ProjectPoint;
            if (onProjectClick && proj.id) {
              onProjectClick(proj.id);
            }
          }}
        >
          {data.map((entry, idx) => (
            <Cell
              key={`cell-${idx}`}
              fill={CATEGORY_COLORS[entry.category]}
              r={dotSize(entry.budget)}
              style={{ cursor: onProjectClick ? 'pointer' : 'default' }}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </ResponsiveContainer>
  );
}
